import { FC, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Grid, Typography } from '@mui/material';

import ErrorMessage from '../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../sharedModules/layout/LoadingWrapper';
import EditorToggle from '../../sharedModules/form/components/EditorToggle';

import axios from '../../sharedModules/utils/axios.utils';

const XoiJobPhotosGrid: FC = () => {
  const { xoiJobId } = useParams<{ xoiJobId: string }>();

  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [photos, setPhotos] = useState<any[]>();
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);

  const fetchPhotosForJob = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/photo_pages/xoi_photos/${xoiJobId}`);
      setPhotos(response.data.photos);
      setDownloadUrl(response.data.proposal_download_url ?? '');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = (photoId: string) => {
    setSelectedPhotoIds((prev) => (prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId]));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response: any = await axios.post(`/photo_pages/xoi_photos`, {
        xoi_job_id: xoiJobId,
        photo_ids: selectedPhotoIds,
      });
      setDownloadUrl(response.data.download_url);
      setErrorMessage('');
    } catch {
      setErrorMessage('Something went wrong saving your selection');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotosForJob();
  }, []);

  return (
    <Grid container>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={loading}>
          {photos?.length > 0 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {photos?.map((photo) => {
                  const selectionIndex = selectedPhotoIds.indexOf(photo.id);
                  const isSelected = selectionIndex !== -1;
                  return (
                    <div
                      key={photo.id}
                      onClick={() => handlePhotoClick(photo.id)}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            zIndex: 1,
                            userSelect: 'none',
                          }}
                        >
                          {selectionIndex + 1}
                        </div>
                      )}
                      <img
                        src={photo.url}
                        alt={`Uploaded by ${photo.uploader}`}
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                          boxSizing: 'border-box',
                        }}
                      />
                      <p style={{ fontSize: '11px', color: '#666', margin: '4px 0 0', wordBreak: 'break-all' }}>
                        {photo.id}
                      </p>
                      <p style={{ fontSize: '11px', color: '#666', margin: '2px 0 0' }}>
                        {photo.uploader} &middot; {new Date(photo.uploaded_at).toLocaleString()}
                      </p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <EditorToggle
                          record={photo}
                          controller="photo_pages/xoi_photos"
                          field="description"
                          afterUpdate={fetchPhotosForJob}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleSave} disabled={selectedPhotoIds.length === 0 || loading}>
                  {`Create Proposal with ${selectedPhotoIds.length} Photo${selectedPhotoIds.length !== 1 ? 's' : ''}`}
                </Button>
                {downloadUrl && (
                  <Button variant="outlined" component="a" href={downloadUrl} target="_blank" rel="noopener noreferrer">
                    View PDF
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Typography>No photos for this Xoi Job</Typography>
          )}
        </LoadingWrapper>
      </Grid>
    </Grid>
  );
};

export default XoiJobPhotosGrid;
